import type { PartRecordEngineRole, PartRecordEntity, PartRecordsResponse } from '@avisoma/shared';

export type ElsewhereEngine = { engineKey: string; engineRole: PartRecordEngineRole };
export type ElsewhereEntity = {
  reconstructedDocumentId: string;
  category: string;
  documentDate: string | null;
  engines: ElsewhereEngine[];
};
export type ElsewhereProject = {
  projectId: string;
  organisationName: string | null;
  workingEsn: string | null;
  entities: ElsewhereEntity[];
};

/** One line per document: its rows (one per graph edge) folded, engines deduped in first-seen order. */
function foldEntities(rows: readonly PartRecordEntity[]): ElsewhereEntity[] {
  const byId = new Map<string, ElsewhereEntity>();
  for (const row of rows) {
    const entity = byId.get(row.reconstructedDocumentId) ?? {
      reconstructedDocumentId: row.reconstructedDocumentId,
      category: row.category,
      documentDate: row.documentDate,
      engines: [],
    };
    const { engineKey, engineRole } = row;
    if (
      engineKey !== null &&
      engineRole !== null &&
      !entity.engines.some((e) => e.engineKey === engineKey && e.engineRole === engineRole)
    ) {
      entity.engines.push({ engineKey, engineRole });
    }
    byId.set(row.reconstructedDocumentId, entity);
  }
  return [...byId.values()];
}

/** Every project but the one whose graph is open, in the server's newest-first order. */
export function otherProjects(response: PartRecordsResponse, currentProjectId: string): ElsewhereProject[] {
  return response.projects
    .filter((project) => project.projectId !== currentProjectId)
    .map((project) => ({
      projectId: project.projectId,
      organisationName: project.organisationName,
      workingEsn: project.workingEsn,
      entities: foldEntities(project.entities),
    }));
}

/** ARC exESN is the engine the part came off (Rinoj, 2026-10-07); a header engine lists it as installed. */
export function engineLabel(engine: ElsewhereEngine): string {
  return engine.engineRole === 'removed-from'
    ? `removed from ESN ${engine.engineKey} (per certificate)`
    : `on ESN ${engine.engineKey}`;
}

/** The other project's graph, focused on the same part node (the graph's `?node=` deep link). */
export function projectGraphHref(projectId: string, partKey: string): string {
  return `/projects/${encodeURIComponent(projectId)}/graph?${new URLSearchParams({ node: partKey })}`;
}
